import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Grid,
  Paper,
  Stack,
  ToggleButton,
  Typography,
} from "@mui/material";
import { IsSmScreen, useAppDispatch, useAppSelector } from "helpers/hooks";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux/es/hooks/useSelector";
import { useLocation, useNavigate } from "react-router-dom";
import { ShLoadingDots } from "shared/SharedComponents/ShLoadingDots/ShLoadingDots";
import { RootState } from "shared/SharedModels";
import { ShBullets } from "shared/SharedStyles/ShDataDisplay";
import { ShGreenBtn, ShToggleButtonGroup } from "shared/SharedStyles/ShInputs";
import {
  completeAtsJobApplication,
  fetchEEOQuestionsJobApply,
  resetCompleteAtsJobApplication,
  updateJobRequirementAnswer,
} from "store/slices/candidate/apply-job/apply-job-questionnaire.slice";
import { completeJobApplication } from "store/slices/candidate/survey/survey-slice";
import { getSelectedJobForm } from "store/slices/employer/employer/employer-details-slice";
import { CandidateLoginRedirect } from "../AuthV2/AuthConstants";
import { useNotification } from "../Notification";
import { ApplyJobAdditionalQuestions } from "./ApplyJobAdditionalQuestions";
import { EqualOpportunityQuestions } from "./ApplyJobEEOQuestions";
import { EEOQutionaareData } from "../CreateJob/EEO/EEOConfig";

export const ApplyJobQuestionnaire = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const notification = useNotification();
  const currentPath = location.pathname + location.search;

  const {
    jobApplicationRequirements,
    isQuestionnaireComplete,
    completeAtsJobApplicationStatus,
    completeAtsJobApplicationResponse,
    eeoQuestions,
    additionalQuestions,
  } = useAppSelector((state) => state.candidate.applyJobQuestionnaire);

  const { eeoQuestionAndAnswers } = useAppSelector((state) => state.candidate.applyJobQuestionnaire);

  const { candidateAssessmentMetadata } = useAppSelector(
    (state) => state.metadata.assessmentMeta
  );
  const { candidateDetails } = useAppSelector(
    (state) => state.candidate.candidateProfile
  );
  const { jobDetailsByCode } = useAppSelector(
    (state) => state.employer.employerJobs.jobInfo
  );
  const { completeJobApplicationStatus, completeJobApplicationResponse } =
    useAppSelector((state) => state.candidate.survey);

  const isSmScreen = IsSmScreen();
  const { selectedJobForm } = useSelector(
    (state: RootState) => state.employer.employerDetails
  );

  const [isAllRequiredAnswered, setIsAllRequiredAnswered] = useState(false);
  const [liveAdditionalAnswers, setLiveAdditionalAnswers] = useState<{
    [key: number]: string;
  }>({});
  const [liveEEOAnswers, setLiveEEOAnswers] = useState<{
    [key: number]: string;
  }>({});

  const handleAllRequiredAnswered = (isValid: boolean) => {
    setIsAllRequiredAnswered(isValid);
  };

  const handleAdditionalAnswerUpdate = (questionId: number, answer: string) => {
    setLiveAdditionalAnswers((prev) => ({
      ...prev,
      [questionId]: answer,
    }));
  };

  const handleEEOAnswerUpdate = (questionId: number, answer: string) => {
    setLiveEEOAnswers((prev) => ({
      ...prev,
      [questionId]: answer,
    }));
  };

  const updateAnswer = (reqId: number, answerId: number) => {
    if (candidateDetails?.jobApplicationId) {
      dispatch(
        updateJobRequirementAnswer({
          jobId: candidateDetails?.jobApplicationId,
          payload: { requirementId: reqId, requirementLevelId: answerId },
        })
      );
    }
  };

  useEffect(() => {
    if (completeAtsJobApplicationStatus === "success") {
      notification.displayNotification({
        open: true,
        type: "success",
        message: completeAtsJobApplicationResponse ?? "",
      });
      dispatch(resetCompleteAtsJobApplication());
      navigate(CandidateLoginRedirect);
    } else if (completeAtsJobApplicationStatus === "failed") {
      notification.displayNotification({
        open: true,
        type: "error",
        message: completeAtsJobApplicationResponse ?? "",
      });
    }
  }, [
    completeAtsJobApplicationResponse,
    completeAtsJobApplicationStatus,
    dispatch,
    navigate,
    notification,
  ]);

  const onSubmitInQuestionnaire = () => {
    if (candidateDetails?.jobApplicationId) {
      if (jobDetailsByCode?.ats_purchased) {
        dispatch(completeAtsJobApplication(candidateDetails?.jobApplicationId));
      } else {
        dispatch(completeJobApplication(candidateDetails?.jobApplicationId));
      }
    }
  };

  useEffect(() => {
    if (completeJobApplicationStatus === "success") {
      notification.displayNotification({
        open: true,
        type: "success",
        message: completeJobApplicationResponse ?? "",
      });
      navigate(CandidateLoginRedirect);
    } else if (completeJobApplicationStatus === "failed") {
      notification.displayNotification({
        open: true,
        type: "error",
        message: completeJobApplicationResponse ?? "",
      });
    }
  }, [
    completeJobApplicationResponse,
    completeJobApplicationStatus,
    dispatch,
    navigate,
    notification,
  ]);

  useEffect(() => {
    if (jobDetailsByCode?.id) {
      dispatch(
        fetchEEOQuestionsJobApply({ jobId: jobDetailsByCode?.id.toString() })
      );
    }
  }, [jobDetailsByCode?.id, dispatch]);

  useEffect(() => {
    if (jobDetailsByCode) {
      dispatch(getSelectedJobForm({ jobId: jobDetailsByCode?.id.toString() }));
    }
  }, [dispatch, jobDetailsByCode]);

  useEffect(() => {
    if (selectedJobForm) {
      const checkAllRequiredAnswered = () => {
        if (additionalQuestions && selectedJobForm) {
          const allAnswered = selectedJobForm?.questions.every((question) => {
            if (question.required !== "required") return true;
            return (
              liveAdditionalAnswers[Number(question.id)] ||
              additionalQuestions[Number(question.id)]?.answer
            );
          });
          setIsAllRequiredAnswered(allAnswered);
        }
      };
      checkAllRequiredAnswered();
    } else {
      setIsAllRequiredAnswered(true);
    }
  }, [additionalQuestions, selectedJobForm, liveAdditionalAnswers]);

  return (
    <>
      <Stack rowGap={2}>
        <Typography variant="body2">
          Please rate your experience level for each of the following questions.
          Your answers will be included in your application.
        </Typography>
        <Stack rowGap={1} minHeight={200} paddingRight={1}>
          {jobApplicationRequirements?.map((requirement) => (
            <Paper variant="outlined" key={requirement.id}>
              <Grid container padding={1} spacing={1} alignItems="center">
                <Grid item xs={12} sm={12} md={12} lg={5} xl={5}>
                  <Typography variant="subtitle2">
                    {requirement.name}
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={12}
                  md={12}
                  lg={7}
                  xl={7}
                  textAlign="end"
                >
                  <ShToggleButtonGroup
                    color="primary"
                    size="small"
                    exclusive
                    minWidth="100px"
                    fullWidth={isSmScreen}
                    orientation={isSmScreen ? "vertical" : "horizontal"}
                    disabled={
                      requirement?.updateJobRequirementAnswerStatus ===
                      "pending"
                    }
                    value={requirement.applicant_requirement_level_id}
                  >
                    {candidateAssessmentMetadata?.skills[
                      requirement.requirement_category_id
                    ].map((option) => (
                      <ToggleButton
                        value={option.id}
                        key={option.id}
                        onClick={() =>
                          option.id !==
                            requirement.applicant_requirement_level_id &&
                          updateAnswer(requirement.id, option.id)
                        }
                      >
                        {requirement?.updateJobRequirementAnswerStatus ===
                          "pending" &&
                          requirement.updateJobRequirementAnswerId ===
                            option.id && <ShLoadingDots variant="positive" />}
                        <Typography variant="body2">{option.name}</Typography>
                      </ToggleButton>
                    ))}
                  </ShToggleButtonGroup>
                </Grid>
              </Grid>
            </Paper>
          ))}
        </Stack>

        {selectedJobForm && (
          <Accordion variant="outlined" defaultExpanded>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="preview-content"
              id="additional-questions"
            >
              <Typography variant="subtitle1" fontWeight="bold">
                Answer these additional questions from the employer.
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <ApplyJobAdditionalQuestions
                selectedJobForm={selectedJobForm}
                onFormValidation={handleAllRequiredAnswered}
                onAnswerUpdate={handleAdditionalAnswerUpdate}
              />
            </AccordionDetails>
          </Accordion>
        )}
        {eeoQuestions && eeoQuestions?.length > 0 && (
          <Accordion variant="outlined" defaultExpanded>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="preview-content"
              id="eeo-questions"
            >
              <Stack spacing={2} direction="column">
                <Typography variant="h6">
                  Voluntary self-identification questions from the employer
                </Typography>

                <Typography variant="body2" color="textSecondary">
                  These questions are coming from the employer. Review the employer's specific privacy policies on their career site.
                  Your responses will not appear in the review section and will not be used by Indeed.
                </Typography>

                <Typography variant="body2" color="textSecondary">
                  Your data is, and remains, confidential and secure. Your answers will be maintained confidentially, and answering any of the below will not negatively impact you in any way. 
                  If there are certain questions you don't want to answer, there is a "Prefer not to say" option.
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Answers to the following optional questions will not impact your application in any way, are strictly confidential and will not be displayed to the hiring team (unless specified).
                  Your responses may become a part of your employee profile if hired.
                </Typography>

                <Typography variant="subtitle1" fontWeight="bold">
                  Answer these Equal Employment Opportunity (EEO) questions from the employer.
                </Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Stack rowGap={1} minHeight={200} paddingRight={1}>
                <EqualOpportunityQuestions 
                  eeoQuestions={eeoQuestions} 
                  onAnswerUpdate={handleEEOAnswerUpdate}
                />
              </Stack>
            </AccordionDetails>
          </Accordion>
        )}

        <Accordion variant="outlined">
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="preview-content"
            id="preview-header"
          >
            <Typography variant="body2">Preview</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="subtitle2" gutterBottom>
              Job Requirements
            </Typography>
            <ShBullets>
              {jobApplicationRequirements?.map((requirement) => (
                <li key={requirement.id}>
                  <Typography variant="caption">
                    {requirement.name} :&nbsp;
                    <strong>
                      {
                        candidateAssessmentMetadata?.skills[
                          requirement.requirement_category_id
                        ]?.find(
                          (option) =>
                            option.id ===
                            requirement.applicant_requirement_level_id
                        )?.name
                      }
                    </strong>
                  </Typography>
                </li>
              ))}
            </ShBullets>
            <Typography variant="subtitle2" mt={2} gutterBottom>
              Additional Questions
            </Typography>
            <ShBullets>
              {selectedJobForm?.questions?.length ? (
                selectedJobForm.questions.map((requirement) => (
                  <li key={requirement.id}>
                    <Typography variant="caption">
                      {requirement.question} :&nbsp;
                      <strong>
                        {liveAdditionalAnswers[Number(requirement.id)] || 
                         additionalQuestions?.[Number(requirement.id)]?.answer || 
                         "Not Answered"}
                      </strong>
                    </Typography>
                  </li>
                ))
              ) : (
                <li>
                  <Typography variant="caption">
                    No additional questions
                  </Typography>
                </li>
              )}
            </ShBullets>

            <Typography variant="subtitle2" mt={2} gutterBottom>
              Equal Employment Opportunity Questionnaire
            </Typography>
            <ShBullets>
              {eeoQuestions?.length ? (
                EEOQutionaareData.filter((q) =>
                  eeoQuestions.includes(q.idx)
                ).map((question) => (
                  <li key={question.id}>
                    <Typography variant="caption">
                      {question.label} :&nbsp;
                      <strong>
                        {liveEEOAnswers[question.idx] || 
                         eeoQuestionAndAnswers?.[question.idx] || 
                         "Not Answered"}
                      </strong>
                    </Typography>
                  </li>
                ))
              ) : (
                <li>
                  <Typography variant="caption">
                    No EEO questions answered
                  </Typography>
                </li>
              )}
            </ShBullets>
          </AccordionDetails>
        </Accordion>

        <Stack direction="row" justifyContent="flex-end">
          {jobDetailsByCode?.ats_purchased ? (
            <ShGreenBtn
              size="small"
              disabled={!isQuestionnaireComplete || !isAllRequiredAnswered}
              variant="outlined"
              onClick={onSubmitInQuestionnaire}
            >
              {completeAtsJobApplicationStatus === "pending"
                ? "Submitting Application..."
                : "Submit"}
            </ShGreenBtn>
          ) : (
            <ShGreenBtn
              size="small"
              disabled={
                !isAllRequiredAnswered ||
                !isQuestionnaireComplete ||
                completeAtsJobApplicationStatus === "pending"
              }
              variant="outlined"
              onClick={() =>
                navigate(
                  `${currentPath.replace("/questionnaire", `/assessment`)}`
                )
              }
            >
              Next
            </ShGreenBtn>
          )}
        </Stack>
      </Stack>
    </>
  );
};
